import { IMG } from "./images";

// Editorial guides for /guides/:slug
export const GUIDES = [
  {
    slug: "history",
    title: "A Short History of the Colosseum",
    subtitle: "From Flavian ambition to global icon — nearly two thousand years in the making.",
    category: "History",
    readTime: "7 min read",
    heroImage: IMG.historic,
    intro: "Few buildings carry as much history as the Colosseum. Commissioned by emperors, soaked in spectacle, ravaged by earthquakes and reborn as a symbol of Rome itself — its story is the story of the city.",
    blocks: [
      {
        type: "prose",
        heading: "An emperor's gift to Rome",
        paragraphs: [
          "Construction began around AD 72 under Emperor Vespasian, founder of the Flavian dynasty, on the drained lake of Nero's extravagant Golden House — a deliberate gift of public spectacle in place of imperial excess. His son Titus inaugurated it around AD 80 with a hundred days of games.",
          "Officially the Flavian Amphitheatre, it later took the name 'Colosseum' from a colossal bronze statue of Nero that once stood nearby.",
        ],
      },
      {
        type: "quote",
        text: "As long as the Colosseum stands, Rome shall stand; when the Colosseum falls, Rome shall fall.",
        author: "Medieval saying, attributed to the Venerable Bede",
      },
      {
        type: "prose",
        heading: "Centuries of spectacle and decline",
        paragraphs: [
          "For some 400 years the arena hosted gladiatorial combat, animal hunts and staged dramas. As the Western Empire faded, the games ceased and the building was repurposed — as housing, a fortress, even a quarry, its marble stripped for churches and palaces.",
          "Earthquakes toppled its southern flank, giving the Colosseum the asymmetric silhouette we know today. Only in modern times did preservation and excavation reveal the underground and restore its standing as a monument.",
        ],
      },
      {
        type: "stats",
        items: [
          { value: "AD 72", label: "Construction begins" },
          { value: "AD 80", label: "Inaugurated by Titus" },
          { value: "~400 yrs", label: "Of games held" },
        ],
      },
    ],
    relatedTickets: ["guided-tours", "underground", "entry"],
    relatedGuides: ["gladiators", "first-time"],
    relatedPlanning: [
      { label: "Colosseum Architecture", to: "/highlights/architecture" },
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
    ],
  },
  {
    slug: "gladiators",
    title: "Gladiators at the Colosseum",
    subtitle: "Who they really were, how they fought, and the myths worth retiring.",
    category: "History",
    readTime: "6 min read",
    heroImage: IMG.interiorCrowd,
    intro: "Gladiators are the Colosseum's enduring stars — but the reality of their lives was more complex, and more human, than the movies suggest. Here's what the evidence actually tells us.",
    blocks: [
      {
        type: "prose",
        heading: "Not all slaves, not all doomed",
        paragraphs: [
          "Many gladiators were enslaved prisoners or convicts, but a surprising number were free volunteers drawn by fame and prize money. They trained in specialised schools under strict regimes, and were valuable investments — owners had little interest in seeing them needlessly killed.",
          "Contrary to popular belief, fights to the death were the exception, not the rule. A defeated fighter could be spared, and skilled gladiators could win their freedom, symbolised by the wooden sword, the rudis.",
        ],
      },
      {
        type: "tips",
        heading: "Types of gladiator",
        items: [
          { icon: "Shield", title: "Murmillo", text: "Heavily armoured, with a large shield and a fish-crested helmet." },
          { icon: "Swords", title: "Thraex", text: "Agile fighter with a curved blade and small square shield." },
          { icon: "Anchor", title: "Retiarius", text: "Lightly armed with a net and trident, relying on speed." },
        ],
      },
      {
        type: "prose",
        heading: "The thumb and the crowd",
        paragraphs: [
          "The fate of a beaten gladiator often rested with the editor — the games' sponsor — who read the mood of the crowd. The famous thumb gesture's exact meaning is still debated by historians, but the spectacle of mass judgement was very real.",
          "Successful gladiators became celebrities, their names scrawled across Roman walls much like modern sports stars.",
        ],
      },
    ],
    relatedTickets: ["underground", "arena-floor", "guided-tours"],
    relatedGuides: ["history", "first-time"],
    relatedPlanning: [
      { label: "The Underground", to: "/highlights/underground" },
      { label: "The Arena Floor", to: "/highlights/arena-floor" },
    ],
  },
  {
    slug: "first-time",
    title: "How to Visit the Colosseum for the First Time",
    subtitle: "A calm, step-by-step plan for a smooth and memorable first visit.",
    category: "Planning",
    readTime: "8 min read",
    heroImage: IMG.womanColosseum,
    intro: "First visit to the Colosseum? This is the only walkthrough you need — from choosing the right ticket to what happens at the gate and how to structure your day.",
    blocks: [
      {
        type: "steps",
        heading: "Your first-visit game plan",
        items: [
          { title: "1. Choose your ticket", text: "Decide how deep you want to go. Skip-the-line suits most first-timers; add a guide for context, or go underground for the full experience." },
          { title: "2. Book in advance", text: "Reserve a morning time slot online. Booking ahead guarantees entry and dodges the long standby queue." },
          { title: "3. Plan your arrival", text: "Take Metro Line B to Colosseo and aim to reach security 15–20 minutes before your slot." },
          { title: "4. Pass security", text: "Travel light — large bags aren't allowed. Have your QR code ready and ID handy." },
          { title: "5. Explore in order", text: "Start with the Colosseum, then walk to the Forum and up Palatine Hill while you still have energy." },
        ],
      },
      {
        type: "alert",
        tone: "info",
        title: "Half a day, well spent",
        text: "Budget around four hours for all three sites at a relaxed pace. Add a Monti lunch afterwards and you have a perfect Roman morning.",
      },
      {
        type: "tips",
        heading: "First-timer essentials",
        items: [
          { icon: "Sunrise", title: "Go early", text: "The first slot is cooler, quieter and best for photos." },
          { icon: "Footprints", title: "Comfortable shoes", text: "You'll cover uneven ground across three sites." },
          { icon: "Headphones", title: "Get the context", text: "A guide or audio guide makes the ruins come alive." },
        ],
      },
    ],
    relatedTickets: ["skip-the-line", "guided-tours", "entry"],
    relatedGuides: ["with-kids", "history"],
    relatedPlanning: [
      { label: "Opening Hours", to: "/plan/opening-hours" },
      { label: "How to Reach", to: "/plan/how-to-reach" },
      { label: "Tips for Visiting", to: "/plan/tips" },
    ],
  },
  {
    slug: "with-kids",
    title: "Visiting the Colosseum with Kids",
    subtitle: "Keep young explorers engaged, comfortable and excited about ancient Rome.",
    category: "Family",
    readTime: "6 min read",
    heroImage: IMG.touristsSmiling,
    intro: "The Colosseum can be a genuine highlight for children — gladiators, secret tunnels and a two-thousand-year-old stadium spark the imagination. A little planning keeps the day fun for everyone.",
    blocks: [
      {
        type: "tips",
        heading: "Make it work for kids",
        items: [
          { icon: "Sunrise", title: "Early start", text: "Visit first thing while energy is high and the site is cool." },
          { icon: "Zap", title: "Skip the queue", text: "A skip-the-line ticket prevents restless waiting in the sun." },
          { icon: "BookOpen", title: "Tell stories", text: "Frame it as the arena where real gladiators fought — kids love the drama." },
          { icon: "Apple", title: "Snacks & water", text: "Pack supplies; nothing is sold inside and re-entry isn't allowed." },
        ],
      },
      {
        type: "prose",
        heading: "Tickets and ages",
        paragraphs: [
          "EU citizens and residents under 18 generally enter free but still need a reserved ticket; non-EU children usually get a reduced rate. Always carry ID to prove age.",
          "For younger children, a standard or family guided tour is more comfortable than the underground, which involves steps and tight spaces and may have a minimum age.",
        ],
      },
      {
        type: "alert",
        tone: "warning",
        title: "Mind the heat and the stairs",
        text: "Summer middays are hot with little shade, and there's plenty of walking on uneven ground. Hats, water and a relaxed pace go a long way with younger visitors.",
      },
    ],
    relatedTickets: ["family", "skip-the-line", "guided-tours"],
    relatedGuides: ["first-time", "history"],
    relatedPlanning: [
      { label: "Restaurants Nearby", to: "/plan/restaurants" },
      { label: "Rules & Regulations", to: "/plan/rules" },
      { label: "Tips for Visiting", to: "/plan/tips" },
    ],
  },
  {
    slug: "colosseum-vatican-one-day",
    title: "Colosseum & Vatican in One Day",
    subtitle: "An ambitious but achievable itinerary for Rome's two greatest sights.",
    category: "Itinerary",
    readTime: "9 min read",
    heroImage: IMG.heroGolden,
    intro: "Seeing both the Colosseum and the Vatican in a single day is demanding — but with a smart plan, early starts and pre-booked tickets, it's entirely possible. Here's how to do it without burning out.",
    blocks: [
      {
        type: "steps",
        heading: "A realistic one-day plan",
        items: [
          { title: "8:30 — Colosseum", text: "Take the very first morning slot. Skip-the-line entry gets you straight in while it's cool and quiet." },
          { title: "10:30 — Forum & Palatine", text: "Walk through the Roman Forum and up Palatine Hill, included with your ticket." },
          { title: "12:30 — Lunch in Monti", text: "Refuel with a relaxed lunch before crossing the city." },
          { title: "14:00 — Travel to the Vatican", text: "Metro Line B to Termini, change to Line A for Ottaviano. Allow 40 minutes." },
          { title: "15:00 — Vatican Museums", text: "An afternoon timed entry to the Museums and Sistine Chapel avoids the morning rush." },
          { title: "18:00 — St. Peter's Square", text: "Finish in the great piazza as the day's light softens." },
        ],
      },
      {
        type: "alert",
        tone: "warning",
        title: "Booking is non-negotiable",
        text: "This itinerary only works with pre-booked, skip-the-line, timed tickets for both sites. Without them, queues alone could consume your entire day.",
      },
      {
        type: "tips",
        heading: "Make it survivable",
        items: [
          { icon: "Ticket", title: "Consider a combo", text: "A Colosseum + Vatican combo ticket simplifies logistics and saves money." },
          { icon: "Shirt", title: "Vatican dress code", text: "Shoulders and knees must be covered — dress accordingly from the morning." },
          { icon: "Footprints", title: "Pace yourself", text: "It's a lot of walking. Comfortable shoes and water are essential." },
        ],
      },
    ],
    relatedTickets: ["combo", "skip-the-line", "guided-tours"],
    relatedGuides: ["first-time", "history"],
    relatedPlanning: [
      { label: "Nearby Attractions", to: "/plan/nearby-attractions" },
      { label: "How to Reach", to: "/plan/how-to-reach" },
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
    ],
  },
];

export const getGuide = (slug) => GUIDES.find((g) => g.slug === slug);
